// create-listings/PaymentModal.jsx


import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useAuth } from "../../hooks/useAuth";
import { getFirestore, doc, setDoc } from "firebase/firestore";

/* ─────────────────── ENV COUPON (trim + uppercase for safe compare) ─────────────────── */
const COUPON_CODE = (process.env.REACT_APP_COUPON_CODE || "Freeaccess2026")
  .trim()
  .toUpperCase();

const PaymentModal = ({ open, handleClose, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  const firestore = getFirestore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [price] = useState(5.00);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [requiresCard, setRequiresCard] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);

  /* ───── coupon ───── */
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);

  const API_URL =
    process.env.REACT_APP_API_URL;
  // process.env.REACT_APP_API_URL || "https://ai-realtor-stripe-fyre.onrender.com";
  // process.env.REACT_APP_API_URL || "http://localhost:9000";


  /* ───────── fetch stored customerId when modal opens ───────── */
  useEffect(() => {
    if (!open) return;

    const fetchCustomerId = async () => {
      try {
        const resp = await fetch(
          `${API_URL}/api/payments/get-customer-id?email=${email}`
        );
        const data = await resp.json();
        if (data.customerId) {
          setCustomerId(data.customerId);
          console.log(`[INFO] Retrieved Stripe customer ID: ${data.customerId}`);
        } else {
          console.log("[INFO] No stored Stripe customer ID, creating new one.");
        }
      } catch (err) {
        console.error("[ERROR] Failed to fetch customer ID:", err);
      }
    };

    fetchCustomerId();
  }, [open, email, API_URL]);

  /* ───────── fetch payment intent once customerId is retrieved ───────── */
  useEffect(() => {
    if (!(open && customerId)) return;

    fetch(`${API_URL}/api/payments/create-payment-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount: price,
        currency: "usd",
        customerId,
        email,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setClientSecret(data.clientSecret);
        setRequiresCard(data.requiresCard);
        if (!data.requiresCard) {
          // auto-charged
          setPaymentSuccess(true);
          setTimeout(() => {
            onPaymentSuccess();
            handleClose();
          }, 2000);
        }
      })
      .catch(() => setError("Failed to initialize payment."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, price, customerId, email, API_URL]);

  /* ───────── Stripe card payment flow ───────── */
  const handlePayment = async () => {
    if (
      !stripe ||
      !clientSecret ||
      (requiresCard && (!elements || !cardComplete))
    )
      return;

    setLoading(true);
    setError(null);

    try {
      let paymentResult = null;

      if (requiresCard) {
        paymentResult = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { email },
          },
        });
      }

      if (paymentResult?.error) {
        setError(paymentResult.error.message);
      } else if (
        !requiresCard ||
        paymentResult.paymentIntent.status === "succeeded"
      ) {
        setPaymentSuccess(true);

        if (requiresCard) {
          fetch(`${API_URL}/api/payments/save-payment-method`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customerId,
              paymentMethodId: paymentResult.paymentIntent.payment_method,
            }),
          });
        }

        setTimeout(() => {
          onPaymentSuccess();
          handleClose();
        }, 2000);
      }
    } catch {
      setError("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ───────── coupon redemption ───────── */
  const handleCoupon = async () => {
    if (coupon.trim().toUpperCase() !== COUPON_CODE) {
      setCouponMsg("Invalid coupon");
      return;
    }
    try {
      setLoading(true);
      const freeUntil = Date.now() + 365 * 24 * 60 * 60 * 1000; // +1 year
      await setDoc(
        doc(firestore, "users", currentUser.uid),
        { freeListingUntil: freeUntil },
        { merge: true }
      );
      onPaymentSuccess(); // mark “paid”
      handleClose();
    } catch {
      setCouponMsg("Could not apply coupon. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="payment-modal">
      <Box
        sx={{
          width: 400,
          p: 3,
          bgcolor: "white",
          borderRadius: 2,
          mx: "auto",
          mt: "20%",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {paymentSuccess ? "Success!" : "Payment Details"}
        </Typography>

        {/* ───── coupon UI ───── */}
        <TextField
          fullWidth
          label="Coupon Code"
          value={coupon}
          onChange={(e) => {
            setCoupon(e.target.value);
            setCouponMsg(null);
          }}
          sx={{ mb: 2 }}
        />
        <Button
          variant="outlined"
          fullWidth
          disabled={!coupon || loading}
          onClick={handleCoupon}
          sx={{ mb: 2 }}
        >
          Apply Coupon
        </Button>
        {couponMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {couponMsg}
          </Alert>
        )}

        {/* ───── Stripe fallback ───── */}
        {!requiresCard ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Payment successful! Charged automatically.
          </Alert>
        ) : (
          <>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <CardElement
              options={{ hidePostalCode: true }}
              onChange={(e) => setCardComplete(e.complete)}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handlePayment}
              disabled={loading || !clientSecret || !cardComplete}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : `Pay $${price} & Save Card`}
            </Button>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default PaymentModal;
